/**
 * The single place that answers, for one POS line at the active Location: **is this line
 * backed by stock, and how many units can it sell?**
 *
 * The answer used to live in four places with four rules — the simple product checked on
 * add-to-cart, the variant in its selector, the combo by how many units its components could
 * assemble, the presentation by its base product — and the quantity caps repeated the same
 * arithmetic in three more branches. Building a cart no longer asks any of it: under ADR-0009
 * the cart accepts anything and **Cobrar is the only stock gate**, so the seam now has one
 * production caller, {@link linesShortOnStock}, which measures the whole cart at once.
 *
 * The seam is deliberately pure and structural: it reads the stock snapshot the POS already
 * holds in memory (the products signal, plus the add-time snapshot carried on a cart line) and
 * performs no reads of its own. It is therefore **advisory** — the authoritative stock gate
 * stays inside the `createSale` transaction, the same advisory/authoritative split ADR-0007
 * uses for held orders.
 */

/**
 * Quantity reported for lines that consume no stock. A finite sentinel rather than `Infinity`,
 * kept from the POS's historical cap so arithmetic on it stays well-behaved.
 */
export const UNLIMITED_STOCK = 999999;

/**
 * A product as the POS knows it at the active Location — the structural slice of
 * `ProductWithStock` this seam needs. Kept structural so the seam stays decoupled from the
 * service's richer type (the same approach `stock-revalidation.utils` takes).
 */
export interface StockHolder {
  id?: string;
  /** Services hold no stock. */
  isService?: boolean;
  /** Stock at the active Location; `undefined` means no figure is known for it. */
  stockAtLocation?: number;
  variants?: { id?: string; stockAtLocation?: number }[];
}

/** A cart line, or a candidate for one (a product about to be added). Both shapes are accepted. */
export interface StockLine {
  id?: string;
  isService?: boolean;
  isCombo?: boolean;
  isPresentation?: boolean;
  /** The synthetic mode-A payment-method surcharge line (ADR-0006), which is not a product. */
  isSurchargeLine?: boolean;
  variantId?: string;
  /**
   * Stock snapshot carried by a cart line. Preferred over the catalog when it says anything —
   * see {@link snapshotAnswers} for when a `0` counts as saying something.
   */
  stockAtLocation?: number;
  /**
   * The snapshot came from a real read rather than the add-to-cart default, so a `0` on it is a
   * measurement and not the absence of one.
   */
  stockMeasured?: boolean;
  comboComponents?: { productId: string; variantId?: string; quantity: number }[];
  baseProductId?: string;
  presentationUnit?: number;
}

/** What the location's current stock can back for one line. */
export interface LineStockAvailability {
  /** The line consumes no stock at all: a service, or a combo whose components are all services. */
  untracked: boolean;
  /**
   * The holder is visible in the catalog but carries no quantity for the active Location —
   * stock was never registered there. Distinct from a holder that *is* registered and simply
   * sold out or short.
   */
  unregistered: boolean;
  /**
   * The holder could not be found at all: it is absent from the catalog (a combo component or
   * a presentation base the POS never loaded), or the line is malformed. The POS product list
   * is paginated and reset by every search, so this means **"we do not know"**, not "no stock" —
   * a caller that blocks on shortage must let these lines through to the transaction, which
   * reads the real documents.
   */
  unmeasurable: boolean;
  /** How many units of **this line** the current stock can back. `UNLIMITED_STOCK` when untracked. */
  maxQuantity: number;
}

/** The answer to "may this line sell N units?" — the ways it can fail, plus success. */
export type LineStockVerdict =
  | 'untracked'
  | 'unmeasurable'
  | 'unregistered'
  | 'out-of-stock'
  | 'insufficient'
  | 'ok';

const UNTRACKED: LineStockAvailability = {
  untracked: true,
  unregistered: false,
  unmeasurable: false,
  maxQuantity: UNLIMITED_STOCK,
};

const UNMEASURABLE: LineStockAvailability = {
  untracked: false,
  unregistered: false,
  unmeasurable: true,
  maxQuantity: 0,
};

/**
 * Does this line hold stock of its own, 1:1 with a single product or variant?
 *
 * The negative cases are the same four this module dispatches on below: a service and the
 * synthetic surcharge line consume nothing, and a combo's components and a presentation's base
 * product are the real holders. Exported because callers that write a figure *onto* a line —
 * rather than measure one — need exactly this taxonomy, and a line kind classified one way here
 * and another way there is how the two drift apart.
 */
export function holdsOwnStock(line: StockLine): boolean {
  return !line.isService && !line.isSurchargeLine && !line.isCombo && !line.isPresentation;
}

/**
 * Resolve how much of `line` the active Location's stock can back, covering all four product
 * types: simple, variant, combo and presentation.
 */
export function resolveLineStockAvailability(
  line: StockLine,
  catalog: readonly StockHolder[]
): LineStockAvailability {
  if (line.isService) {
    return UNTRACKED;
  }
  // A combo whose components never loaded falls through to its own stock snapshot — the
  // fallback the POS quantity cap has always used for that case.
  if (line.isCombo && line.comboComponents) {
    return resolveComboAvailability(line.comboComponents, catalog);
  }
  if (line.isPresentation) {
    return resolvePresentationAvailability(line, catalog);
  }
  return resolveSimpleAvailability(line, catalog);
}

/** Classify a requested quantity against an availability. `quantity` is in the line's own units. */
export function verdictFor(
  availability: LineStockAvailability,
  quantity: number
): LineStockVerdict {
  if (availability.untracked) {
    return 'untracked';
  }
  if (availability.unmeasurable) {
    return 'unmeasurable';
  }
  if (availability.maxQuantity <= 0) {
    return availability.unregistered ? 'unregistered' : 'out-of-stock';
  }
  return quantity > availability.maxQuantity ? 'insufficient' : 'ok';
}

/**
 * A cart line as the checkout pre-check sees it: a {@link StockLine} plus the units it asks for.
 */
export interface QuantifiedStockLine extends StockLine {
  quantity: number;
}

/** A line the Location cannot back, and by how much. */
export interface LineShortage<T> {
  line: T;
  /** Units this line asks for. */
  requested: number;
  /** Units the Location can back for it — 0 when it holds none at all. */
  available: number;
}

/**
 * The lines the loaded catalog says the active Location cannot back, in cart order, each with
 * the numbers behind the verdict so the caller can tell the cashier *how* short it is. An empty
 * result means checkout may proceed — the stock gate behind ADR-0009, which stops a Sale from
 * carrying lines only a Quote may carry.
 *
 * Advisory twice over: the snapshot may be stale, and a line whose holder the POS never loaded
 * comes back `unmeasurable` and is let through rather than blocked, so the check never rejects
 * a sale the shop can actually make. The `createSale` transaction stays the authority.
 */
export function linesShortOnStock<T extends QuantifiedStockLine>(
  lines: readonly T[],
  catalog: readonly StockHolder[]
): LineShortage<T>[] {
  const shortages: LineShortage<T>[] = [];

  for (const line of lines) {
    // A line asking for nothing — or returning goods — consumes no stock.
    if (line.quantity <= 0) {
      continue;
    }
    const availability = resolveLineStockAvailability(line, catalog);
    const verdict = verdictFor(availability, line.quantity);
    if (verdict === 'out-of-stock' || verdict === 'unregistered' || verdict === 'insufficient') {
      shortages.push({ line, requested: line.quantity, available: availability.maxQuantity });
    }
  }

  return shortages;
}

/**
 * Does the line's own snapshot answer the question, or must we ask the catalog?
 *
 * Any non-zero figure answers: it was captured with fresh data when the line was created, and a
 * line added that way should not be re-measured against a possibly staler products signal. A
 * **zero** is the ambiguous one, because `addToCart` falls back to `0` whenever it has no figure
 * to record — so a zero answers only when the line was re-stamped from a real read
 * (`stockMeasured`). Without that distinction a line correctly refreshed to 0 would be handed
 * back to a catalog that may still be carrying the pre-sale figure, and the cart would show 0
 * while Cobrar waved the line through.
 */
function snapshotAnswers(line: StockLine): boolean {
  return (line.stockAtLocation ?? 0) !== 0 || !!line.stockMeasured;
}

/** Simple product or variant: the line's own snapshot wins whenever it answers. */
function resolveSimpleAvailability(
  line: StockLine,
  catalog: readonly StockHolder[]
): LineStockAvailability {
  if (snapshotAnswers(line)) {
    return backedBy(line.stockAtLocation);
  }

  const product = findHolder(catalog, line.id);
  if (!product) {
    return UNMEASURABLE;
  }
  if (!line.variantId) {
    return backedBy(product.stockAtLocation);
  }

  // A variant line reads its own variant. If the variants were never loaded, or this one is
  // gone from them, the base product's figure would answer a different question.
  const variant = product.variants?.find(v => v.id === line.variantId);
  return variant ? backedBy(variant.stockAtLocation) : UNMEASURABLE;
}

/** Availability of a holder we can see, from the quantity it carries for the active Location. */
function backedBy(stock: number | undefined): LineStockAvailability {
  return {
    untracked: false,
    unregistered: stock === undefined,
    unmeasurable: false,
    maxQuantity: Math.max(0, stock ?? 0),
  };
}

/** Combo: how many whole combos the scarcest component can assemble. Service components don't limit it. */
function resolveComboAvailability(
  components: NonNullable<StockLine['comboComponents']>,
  catalog: readonly StockHolder[]
): LineStockAvailability {
  // A combo that declares no components at all describes nothing we can measure — it is a
  // data problem, not an unlimited combo. (The POS product search has dropped
  // `comboComponents` before now, so this is reachable.)
  if (components.length === 0) {
    return UNMEASURABLE;
  }

  let maxQuantity = Infinity;
  let unregistered = false;

  for (const component of components) {
    const product = findHolder(catalog, component.productId);
    if (!product) {
      // A component we cannot see leaves the whole combo unmeasurable.
      return UNMEASURABLE;
    }
    if (product.isService) {
      continue;
    }

    let stock: number | undefined;
    if (component.variantId) {
      const variant = product.variants?.find(v => v.id === component.variantId);
      if (!variant) {
        return UNMEASURABLE;
      }
      stock = variant.stockAtLocation;
    } else {
      stock = product.stockAtLocation;
    }
    if (stock === undefined) {
      unregistered = true;
    }

    maxQuantity = Math.min(maxQuantity, Math.floor((stock ?? 0) / component.quantity));
  }

  // Every component was a service: nothing to consume.
  if (maxQuantity === Infinity) {
    return UNTRACKED;
  }

  return {
    untracked: false,
    unregistered,
    unmeasurable: false,
    maxQuantity: Math.max(0, maxQuantity),
  };
}

/** Presentation: how many packs the base product's stock fills. Variant-based ones follow the base product. */
function resolvePresentationAvailability(
  line: StockLine,
  catalog: readonly StockHolder[]
): LineStockAvailability {
  if (!line.baseProductId || !line.presentationUnit) {
    return UNMEASURABLE;
  }

  const baseProduct = findHolder(catalog, line.baseProductId);
  if (!baseProduct) {
    return UNMEASURABLE;
  }
  if (baseProduct.isService) {
    return UNTRACKED;
  }

  const stock = baseProduct.stockAtLocation;
  return {
    untracked: false,
    unregistered: stock === undefined,
    unmeasurable: false,
    maxQuantity: Math.max(0, Math.floor((stock ?? 0) / line.presentationUnit)),
  };
}

function findHolder(
  catalog: readonly StockHolder[],
  id: string | undefined
): StockHolder | undefined {
  return id ? catalog.find(p => p.id === id) : undefined;
}
